import { type ReactNode, useId } from "react";

import {
  Switch as ReactAriaSwitch,
  type SwitchProps as ReactAriaSwitchProps,
  composeRenderProps,
} from "react-aria-components";

import { FormFieldAssistiveText } from "#components/v2/shared/FormFieldAssistiveText";
import { useUIVersion } from "#hooks";
import { cn, customTv } from "#utils";

import { circleStyles, containerStyles } from "./Switch.styles";

const switchStyles = customTv({
  // `relative` is required so the visually-hidden <input> rendered by
  // react-aria-components (which uses `position: absolute`) is positioned
  // relative to this label rather than the document root. Without it,
  // clicking the switch can scroll the page out of view when the
  // switch is nested inside a scrollable container (e.g. a table).
  base: "relative group flex items-center gap-md text-label-medium font-medium transition text-text-primary",
  variants: {
    isDisabled: {
      true: "text-text-disabled cursor-not-allowed",
    },
    uiVersion: {
      v1: "text-[10px] uppercase",
      v2: "",
    },
  },
});

export interface SwitchProps extends ReactAriaSwitchProps {
  children?: ReactNode;
  /**
   * Applied to the element that lays out in the parent: the switch itself, or
   * the column wrapping the switch and its assistive text when `assistiveText`
   * is set.
   */
  className?: string;
  position?: "leading" | "trailing";
  /** Help text rendered below the switch, linked to it via `aria-describedby` */
  assistiveText?: ReactNode;
}

export function Switch({
  children,
  position = "leading",
  className,
  assistiveText,
  isDisabled,
  "aria-describedby": ariaDescribedBy,
  ...props
}: SwitchProps) {
  const uiVersion = useUIVersion();
  const assistiveTextId = useId();

  const switchElement = (
    <ReactAriaSwitch
      // react-aria's Switch has no `description` slot, so the assistive text is
      // referenced by id rather than picked up from the slot automatically.
      aria-describedby={
        [ariaDescribedBy, assistiveText ? assistiveTextId : null].filter(Boolean).join(" ") ||
        undefined
      }
      className={composeRenderProps(
        // With assistive text the wrapper below is the element that lays out in
        // the parent, so it takes the caller's classes instead.
        assistiveText ? undefined : className,
        (className, renderProps) => switchStyles({ ...renderProps, className, uiVersion }),
      )}
      {...props}
      isDisabled={isDisabled}
    >
      {({ isSelected, isHovered, isFocused, isDisabled }) => (
        <>
          {position === "leading" ? children : null}
          <div
            className={containerStyles({
              isSelected,
              isHovered,
              isFocused,
              isDisabled,
              uiVersion,
            })}
          >
            <div className={circleStyles({ isSelected, isDisabled, uiVersion })} />
          </div>
          {position === "trailing" ? <span>{children}</span> : null}
        </>
      )}
    </ReactAriaSwitch>
  );

  if (!assistiveText) {
    return switchElement;
  }

  return (
    <div className={cn("flex flex-col gap-y-[.15rem]", className)}>
      {switchElement}

      <FormFieldAssistiveText id={assistiveTextId} isDisabled={isDisabled}>
        {assistiveText}
      </FormFieldAssistiveText>
    </div>
  );
}
